import React, { useState, useContext, useEffect, useId, useRef } from 'react';
import {
	TextField,
	Container,
	Button,
	Typography,
	MenuItem,
	Grid,
	InputAdornment,
	Autocomplete,
	Box,
	CircularProgress
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { isEmpty } from 'lodash';
import { Stack } from '@mui/system';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment/moment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { getToken } from '../../api/Account';
import { DataContext } from '../../context/DataContext';
import { getRuta } from '../../helpers/Helpers';
import { formatearMonto } from '../../helpers/formato';
import {
	PageHeader,
	EstadoError,
	SeccionFormulario,
	SelectorFotos,
	useAviso
} from '../../components/base';
import { getClientListAsync } from '../../api/Clients';
import { getPeriodicidadListAsync } from '../../api/Periodicidad';
import { getInteresListAsync } from '../../api/InteresCorriente';
import { getGestoresAsync } from '../../api/Users';
import { getPlazoByPeriodicidadAsync } from '../../api/Plazo';
import { addCreditAsync } from '../../api/Credits';
import CargandoFormulario from './addCredit/CargandoFormulario';

// El cliente se busca por nombre y también por cédula (hay homónimos).
const filtrarClientes = createFilterOptions({
	stringify: (option) => `${option.fullName} ${option.identification ?? ''}`
});

// Un dato del cliente elegido: etiqueta pequeña y valor.
const DatoCliente = ({ etiqueta, valor }) => (
	<Box sx={{ minWidth: 0 }}>
		<Typography variant="body2" sx={{ color: 'text.secondary' }}>
			{etiqueta}
		</Typography>
		<Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
			{valor === null || valor === undefined || valor === '' ? '—' : valor}
		</Typography>
	</Box>
);

const AddCredit = () => {
	const token = getToken();
	const { id } = useParams();
	let ruta = getRuta();
	let navigate = useNavigate();
	const { setIsLoading, setIsUnautorized } = useContext(DataContext);
	const aviso = useAviso();
	const prefijo = useId();

	const [clientList, setClientList] = useState([]);
	const [selectedClient, setSelectedClient] = useState(null);

	const [periodicidadList, setPeriodicidadList] = useState([]);
	const [selectedPeriodicidad, setSelectedPeriodicidad] = useState('');

	const [plazoList, setPlazoList] = useState([]);
	const [selectedPlazo, setSelectedPlazo] = useState('');

	const [interesCorrienteList, setInteresCorrienteList] = useState([]);
	const [selectedInteresCorriente, setSelectedInteresCorriente] = useState('');

	const [gestorList, setGestorList] = useState([]);
	const [selectedGestor, setSelectedGestor] = useState('');
	const [montoPrincipal, setMontoPrincipal] = useState('');
	const [destino, setDestino] = useState('');

	const [fechaPrimeraCuota, setFechaPrimeraCuota] = useState(new Date());
	const [isNominal] = useState(false);

	const [selectedFiles, setSelectedFiles] = useState([]);
	const [previews, setPreviews] = useState([]);

	// Carga de las 4 listas del arranque (en cascada, como siempre): mientras llegan se ve un
	// esqueleto del formulario, y si una falla se dice y se puede reintentar.
	const [cargando, setCargando] = useState(true);
	const [errorCarga, setErrorCarga] = useState(false);
	// Errores de validación junto a cada campo: { clave: 'texto' }.
	const [errores, setErrores] = useState({});
	// El overlay de carga no bloquea el teclado: el ref corta un segundo envío al instante
	// y el estado deshabilita el botón (misma protección que AddAbono).
	const enviando = useRef(false);
	const [guardando, setGuardando] = useState(false);
	const formulario = useRef(null);

	const cargarListas = async () => {
		setCargando(true);
		setErrorCarga(false);
		setIsLoading(true);
		const falla = (resultado) => {
			setIsLoading(false);
			if (resultado.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			setCargando(false);
			setErrorCarga(true);
		};

		const result = await getClientListAsync(token);
		if (!result.statusResponse) {
			falla(result);
			return;
		}
		setClientList(result.data.result);

		if (id != 0) {
			let filtered = result.data.result.filter((item) => item.id == id);
			setSelectedClient(filtered[0] ?? null);
		}

		const resultPer = await getPeriodicidadListAsync(token);
		if (!resultPer.statusResponse) {
			falla(resultPer);
			return;
		}
		setPeriodicidadList(resultPer.data.result);

		const resultGestores = await getGestoresAsync(token);
		if (!resultGestores.statusResponse) {
			falla(resultGestores);
			return;
		}
		setGestorList(resultGestores.data.result);

		const resultIC = await getInteresListAsync(token);
		if (!resultIC.statusResponse) {
			falla(resultIC);
			return;
		}
		setInteresCorrienteList(resultIC.data.result);

		setIsLoading(false);
		setCargando(false);
	};

	useEffect(() => {
		cargarListas();
	}, []);

	// Escribir o elegir en un campo limpia su error.
	const limpiarError = (clave) => {
		if (errores[clave]) setErrores({ ...errores, [clave]: undefined });
	};

	//Devuelve un entero positivo
	const funtionSetMontoSolicitado = (value) => {
		if (/^[0-9]+$/.test(value.toString()) || value === '') {
			setMontoPrincipal(value);
			limpiarError('monto');
			return;
		}
	};

	const handleChangePeriodicidad = async (event) => {
		const selected = periodicidadList.filter((item) => item.id == event.target.value);

		// if (event.target.value === 2) {
		//   var date = new Date();
		//   setFechaPrimeraCuota(new Date(date.getFullYear(), date.getMonth(), 1));
		// } else {
		let dias = selected[0].cuotaDias;
		var newDate = new Date();
		newDate.setDate(newDate.getDate() + dias);
		setFechaPrimeraCuota(newDate);
		// }
		setPlazoList([]);
		setSelectedPeriodicidad(event.target.value);

		if (event.target.value !== '') {
			setIsLoading(true);
			const result = await getPlazoByPeriodicidadAsync(token, event.target.value);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				aviso.error(result.error || 'No se pudieron cargar los plazos');
				return;
			}
			setPlazoList(result.data.result);
			setIsLoading(false);
		} else {
			setPlazoList([]);
		}
	};

	const addNewCredit = async () => {
		if (enviando.current) {
			return;
		}
		if (validate()) {
			enviando.current = true;
			setGuardando(true);
			const formData = new FormData();

			formData.append('clientId', selectedClient.id);
			formData.append('montoPrincipal', parseInt(montoPrincipal));
			formData.append(
				'fechaPrimeraCuota',
				moment(fechaPrimeraCuota).format('YYYY-MM-DD').toString()
			);

			formData.append('destino', destino);
			formData.append('periocidadId', selectedPeriodicidad);
			formData.append('plazoId', selectedPlazo);
			formData.append('interesCorrienteId', selectedInteresCorriente);
			formData.append('gestor', selectedGestor);

			selectedFiles.forEach((photo, index) => {
				formData.append(`creditPhotos`, photo); // o photo si es directamente el File
			});

			setIsLoading(true);
			const result = await addCreditAsync(token, formData);
			enviando.current = false;
			setGuardando(false);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				aviso.error(result.error || 'No se pudo guardar el crédito');
				return;
			}
			setIsLoading(false);
			clearData();
			aviso.exito('Crédito guardado');
		}
	};

	// Validando campos ingresados. Mismas reglas de siempre, en el mismo orden; ahora se muestran
	// todas juntas, cada una junto a su campo, en vez de un aviso a la vez.
	const validate = () => {
		const faltantes = {};
		if (isEmpty(selectedClient)) faltantes.cliente = 'Seleccione un cliente';
		if (montoPrincipal === '') faltantes.monto = 'Ingrese el monto del crédito';
		if (fechaPrimeraCuota === '')
			faltantes.fecha = 'Ingrese la fecha de la primera cuota';
		if (selectedGestor === '') faltantes.gestor = 'Seleccione un gestor';
		if (selectedPlazo === '') faltantes.plazo = 'Seleccione un plazo';
		if (selectedInteresCorriente === '')
			faltantes.tasa = 'Seleccione una tasa de interés';
		if (isEmpty(destino)) faltantes.destino = 'Ingrese el destino del crédito';
		if (selectedFiles.length <= 0) faltantes.fotos = 'Agregue al menos una foto';
		setErrores(faltantes);
		const valido = isEmpty(faltantes);
		if (!valido) {
			// Lleva la vista al primer campo con error.
			setTimeout(() => {
				const primero = formulario.current?.querySelector('[aria-invalid="true"], .Mui-error');
				if (primero) {
					if (typeof primero.focus === 'function') primero.focus();
					if (typeof primero.scrollIntoView === 'function')
						primero.scrollIntoView({ block: 'center' });
				}
			}, 0);
		}
		return valido;
	};

	const clearData = () => {
		setSelectedClient(null);
		setMontoPrincipal('');
		setSelectedGestor('');
		setDestino('');
		setSelectedPeriodicidad('');
		setSelectedPlazo('');
		setSelectedInteresCorriente('');
		setSelectedFiles([]);
		setPreviews([]);
		setErrores({});
	};

	const onChangeFile = (e) => {
		const files = Array.from(e.target.files);

		if (!files) return;

		// Crear vistas previas
		const newPreviews = files.map((file) => ({
			file,
			preview: URL.createObjectURL(file)
		}));

		setSelectedFiles(files);
		setPreviews(newPreviews);
		limpiarError('fotos');
		// Permite volver a elegir el mismo archivo después de quitarlo.
		e.target.value = '';
	};

	const removeImage = (index) => {
		// Revocar la URL para liberar memoria
		URL.revokeObjectURL(previews[index].preview);

		const newPreviews = previews.filter((_, i) => i !== index);
		const newFiles = selectedFiles.filter((_, i) => i !== index);

		setPreviews(newPreviews);
		setSelectedFiles(newFiles);
	};

	// Limpiar URLs cuando el componente se desmonte
	React.useEffect(() => {
		return () => {
			previews.forEach((preview) => URL.revokeObjectURL(preview.preview));
		};
	}, []);

	const textoVacio = (texto) => <em>{texto}</em>;

	return (
		<div>
			<Container maxWidth={false} disableGutters>
				<PageHeader
					titulo="Nuevo crédito"
					subtitulo={
						selectedClient
							? selectedClient.fullName
							: 'Elige el cliente y completa las condiciones'
					}
					acciones={
						<Button
							variant="outlined"
							startIcon={<ArrowBackIcon />}
							onClick={() => {
								navigate(`${ruta}/credits`);
							}}
						>
							Regresar
						</Button>
					}
				/>

				{errorCarga ? (
					<EstadoError
						titulo="No se pudo cargar el formulario"
						mensaje="Faltan datos para crear el crédito. Revisa tu conexión e inténtalo de nuevo."
						onReintentar={cargarListas}
					/>
				) : cargando ? (
					<CargandoFormulario />
				) : (
					<Stack spacing={3} ref={formulario}>
						<SeccionFormulario titulo="Cliente">
							<Grid container spacing={2} sx={{ mt: 0.5 }}>
								<Grid item xs={12}>
									<Autocomplete
										options={clientList}
										getOptionLabel={(option) => option.fullName}
										filterOptions={filtrarClientes}
										isOptionEqualToValue={(option, value) => option.id === value.id}
										value={selectedClient}
										onChange={(event, newValue) => {
											setSelectedClient(newValue);
											limpiarError('cliente');
										}}
										noOptionsText="No existe cliente con ese nombre o cédula"
										renderOption={(props, option) => (
											<li {...props} key={option.id}>
												<Box sx={{ minWidth: 0 }}>
													<Typography>{option.fullName}</Typography>
													<Typography
														variant="body2"
														sx={{
															color: 'text.secondary',
															fontVariantNumeric: 'tabular-nums'
														}}
													>
														{option.identification || 'Sin cédula'}
													</Typography>
												</Box>
											</li>
										)}
										renderInput={(params) => (
											<TextField
												{...params}
												required
												fullWidth
												label="Cliente"
												error={Boolean(errores.cliente)}
												helperText={
													errores.cliente ||
													'Busca por nombre o por cédula'
												}
											/>
										)}
									/>
								</Grid>

								{selectedClient ? (
									<Grid item xs={12}>
										<Box
											sx={{
												p: 2,
												borderRadius: 2.5,
												bgcolor: (theme) => theme.palette.soft.primary,
												display: 'grid',
												gap: 2,
												gridTemplateColumns: {
													xs: 'repeat(2, minmax(0, 1fr))',
													md: 'repeat(4, minmax(0, 1fr))'
												}
											}}
										>
											<DatoCliente etiqueta="Cédula" valor={selectedClient.identification} />
											<DatoCliente etiqueta="Teléfono" valor={selectedClient.phone} />
											<DatoCliente etiqueta="Promedio" valor={selectedClient.promedio} />
											<DatoCliente etiqueta="Id del cliente" valor={selectedClient.id} />
										</Box>
									</Grid>
								) : null}
							</Grid>
						</SeccionFormulario>

						<SeccionFormulario titulo="Condiciones">
							<Grid container spacing={2} sx={{ mt: 0.5 }}>
								<Grid item xs={12} md={6}>
									<TextField
										required
										fullWidth
										onChange={(e) => funtionSetMontoSolicitado(e.target.value)}
										value={montoPrincipal}
										label={'Monto solicitado'}
										error={Boolean(errores.monto)}
										helperText={
											errores.monto ||
											(montoPrincipal !== ''
												? formatearMonto(parseInt(montoPrincipal))
												: ' ')
										}
										inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">C$</InputAdornment>
											)
										}}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<TextField
										required
										fullWidth
										onChange={(e) => {
											setDestino(e.target.value.toUpperCase());
											limpiarError('destino');
										}}
										value={destino}
										label={'Destino'}
										error={Boolean(errores.destino)}
										helperText={errores.destino || ' '}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<TextField
										select
										fullWidth
										id={`${prefijo}-periodicidad`}
										label="Periodicidad"
										onChange={handleChangePeriodicidad}
										value={selectedPeriodicidad}
										disabled={isNominal}
										helperText="Define los plazos disponibles"
									>
										<MenuItem value="">{textoVacio('Seleccione un periodo')}</MenuItem>
										{periodicidadList.map((item) => {
											return (
												<MenuItem key={item.id} value={item.id}>
													{item.description}
												</MenuItem>
											);
										})}
									</TextField>
								</Grid>

								<Grid item xs={12} md={6}>
									<TextField
										select
										required
										fullWidth
										id={`${prefijo}-plazo`}
										label="Plazo"
										onChange={(e) => {
											setSelectedPlazo(e.target.value);
											limpiarError('plazo');
										}}
										value={selectedPlazo}
										error={Boolean(errores.plazo)}
										disabled={plazoList.length === 0}
										helperText={
											errores.plazo ||
											(selectedPeriodicidad === ''
												? 'Elige primero la periodicidad'
												: ' ')
										}
									>
										<MenuItem value="">{textoVacio('Seleccione un plazo')}</MenuItem>
										{plazoList.map((item) => {
											return (
												<MenuItem key={item.id} value={item.id}>
													{item.description}
												</MenuItem>
											);
										})}
									</TextField>
								</Grid>

								<Grid item xs={12} md={6}>
									<TextField
										select
										required
										fullWidth
										id={`${prefijo}-tasa`}
										label="Tasa de interés"
										onChange={(e) => {
											setSelectedInteresCorriente(e.target.value);
											limpiarError('tasa');
										}}
										value={selectedInteresCorriente}
										error={Boolean(errores.tasa)}
										helperText={errores.tasa || ' '}
									>
										<MenuItem value="">
											{textoVacio('Seleccione una tasa de interés')}
										</MenuItem>
										{interesCorrienteList.map((item) => {
											return (
												<MenuItem key={item.id} value={item.id}>
													{`${item.intCorriente.toFixed(2)} %`}
												</MenuItem>
											);
										})}
									</TextField>
								</Grid>

								<Grid item xs={12} md={6}>
									<DesktopDatePicker
										closeOnSelect
										label="Fecha de la primera cuota"
										inputFormat="DD/MM/YYYY"
										value={fechaPrimeraCuota}
										onChange={(newValue) => {
											setFechaPrimeraCuota(newValue);
											limpiarError('fecha');
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												required
												fullWidth
												error={params.error || Boolean(errores.fecha)}
												helperText={errores.fecha || ' '}
											/>
										)}
									/>
								</Grid>
							</Grid>
						</SeccionFormulario>

						<SeccionFormulario titulo="Gestor">
							<Grid container spacing={2} sx={{ mt: 0.5 }}>
								<Grid item xs={12} md={6}>
									<TextField
										select
										required
										fullWidth
										id={`${prefijo}-gestor`}
										label="Gestor"
										onChange={(e) => {
											setSelectedGestor(e.target.value);
											limpiarError('gestor');
										}}
										value={selectedGestor}
										error={Boolean(errores.gestor)}
										helperText={errores.gestor || 'Quien cobrará este crédito'}
									>
										<MenuItem value="">{textoVacio('Seleccione un gestor')}</MenuItem>
										{gestorList.map((item) => {
											return (
												<MenuItem key={item.id} value={item.id}>
													{item.fullName}
												</MenuItem>
											);
										})}
									</TextField>
								</Grid>
							</Grid>
						</SeccionFormulario>

						<SeccionFormulario
							titulo="Fotos"
							descripcion="Fotos del crédito: se necesita al menos una."
						>
							<SelectorFotos
								previews={previews}
								onChange={onChangeFile}
								onRemove={removeImage}
								error={errores.fotos}
								disabled={guardando}
							/>
						</SeccionFormulario>

						<Box
							sx={{
								display: 'flex',
								justifyContent: { xs: 'stretch', sm: 'flex-end' }
							}}
						>
							<Button
								variant="contained"
								size="large"
								disabled={guardando}
								onClick={() => addNewCredit()}
								startIcon={
									guardando ? <CircularProgress size={18} color="inherit" /> : null
								}
								sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 220 }}
							>
								{guardando ? 'Guardando…' : 'Guardar crédito'}
							</Button>
						</Box>
					</Stack>
				)}
			</Container>
		</div>
	);
};

export default AddCredit;
